import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  useTheme,
  Popover,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { useLoaderData, useNavigate } from "react-router-dom";
import {
  GetAccountSubscriptionResponse,
  PlanCodes,
} from "@/types/api/accountSubscription";
import { formatNumber } from "@tiny/utils/formatUtils";
import { getKarmaLevel } from "@/hooks/plans.hooks";
import { PlanLogos } from "../PlanSelection/PlanLogo";

function PlanInfo() {
  const theme = useTheme();
  const navigate = useNavigate();
  const data = useLoaderData() as { planInfo: GetAccountSubscriptionResponse };
  const {
    planName,
    planCode,
    trial,
    price,
    maxCustomerCount,
    currentCustomerCount,
  } = data.planInfo;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpgrade = () => {
    handleClose();
    navigate(`account-settings/plans`);
  };

  const progressPercentage =
    maxCustomerCount && currentCustomerCount
      ? (currentCustomerCount / maxCustomerCount) * 100
      : 0;

  const getProgressColor = () => {
    if (progressPercentage >= 90) return theme.palette.error.main;
    if (progressPercentage >= 70) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <>
      <IconButton onClick={handleOpen} id="plan-logo-button">
        <PlanLogos karmaLevel={getKarmaLevel(planCode as PlanCodes)} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: 2,
            width: 280,
            bgcolor: theme.palette.common.white,
          },
        }}
      >
        <Box borderRadius={2}>
          <Box
            display="flex"
            gap={1}
            pt={3}
            px={2}
            justifyContent="space-between"
          >
            <Typography variant="label1">{planName}</Typography>
            <PlanLogos karmaLevel={getKarmaLevel(planCode as PlanCodes)} />
          </Box>

          {maxCustomerCount && currentCustomerCount !== undefined && (
            <Box pt={4} px={1}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="label2" color="text.secondary">
                  ${price} / month
                </Typography>
                <Typography variant="label2" color="text.secondary">
                  {formatNumber(currentCustomerCount)} /{" "}
                  {formatNumber(maxCustomerCount)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(progressPercentage, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: getProgressColor(),
                  },
                }}
              />
            </Box>
          )}
          <Box
            pt={3}
            pb={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {trial && <Chip label="Trial" size="small" />}
            <Button
              variant="outlined"
              fullWidth
              onClick={handleUpgrade}
              id="sidebar-plan-upgrade"
            >
              Upgrade
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default PlanInfo;
